<template>
  <RevCard
    class="overflow-hidden"
    :class="{ 'outline-default-hi': hasFocus }"
    data-qa="productCard"
    :hoverable="true"
  >
    <div class="pt-16" data-spec="product-card-content">
      <div class="p-16 pt-0" data-spec="main">
        <div class="flex flex-col gap-8 md:flex-row" data-spec="main-content">
          <div
            class="relative mx-auto flex flex-col items-center gap-2 p-16 pb-0"
            :class="{ 'md:w-1/2': !specifications }"
            data-spec="image+colors"
          >
            <div data-spec="image+pill">
              <div
                v-if="percentageDifference"
                class="mood-purple bg-static-default-hi text-static-default-hi rounded-full absolute -translate-x-8 -translate-y-8 -rotate-6 px-12 py-4 text-center"
              >
                <div class="body-2-bold">
                  {{
                    i18n(translations.pillDiscountPercentage, {
                      percent: percentageDifference,
                    })
                  }}
                </div>

                <div class="caption-bold -mt-4">
                  {{ i18n(translations.pillDiscountBody) }}
                </div>
              </div>
              <RevLink :to="url" :underlined="false">
                <RevIllustration
                  :alt="imageAlt"
                  :height="160"
                  :src="imageSrc"
                  :width="160"
                />
              </RevLink>
            </div>

            <ColorSwatches
              v-if="colorSwatches && !specifications"
              :colors="colorSwatches"
            />
          </div>

          <div class="flex grow flex-col gap-6" data-spec="product-info">
            <RevLink data-spec="titles" :to="url" :underlined="false">
              <div class="flex flex-col gap-2">
                <h2 class="body-1-bold line-clamp-2">{{ title }}</h2>
                <h3 v-if="description" class="body-2 line-clamp-2">
                  {{ description }}
                </h3>
                <span v-if="warranty" class="body-2">
                  {{ i18n(translations.productCardWarranty, { warranty }) }}
                </span>
              </div>
            </RevLink>

            <RevLink data-spec="rating" :to="url" :underlined="false">
              <ProductCardRatings
                :count="reviewRating?.count"
                :score="reviewRating?.average"
              />
            </RevLink>

            <RevLink
              data-spec="price-information"
              :to="url"
              :underlined="false"
            >
              <span class="text-action-default-low body-2">
                {{ i18n(translations.startingFrom) }}
              </span>
              <PriceWithCrossedPrice :price="i18n.price(price)" />
            </RevLink>

            <div>
              <RevButtonTiny
                class="mt-12 hidden md:block"
                :icon="IconChevronRight"
                :tabindex="-1"
                variant="primary"
              >
                {{ i18n(translations.cta) }}
              </RevButtonTiny>
            </div>

            <RevIllustration
              alt="Illustration"
              class="hidden"
              :height="64"
              src="/img/product-card/cashHandCroppedRight.svg"
              :width="64"
            />
          </div>

          <div v-if="specifications" class="-mr-24 hidden self-start md:flex">
            <div class="my-auto mr-8 flex flex-col text-right">
              <div class="text-static-brand-mid body-2-bold">
                {{ i18n(translations.explanationTitle) }}
              </div>

              <div class="text-static-brand-mid body-2">
                {{ i18n(translations.explanationBody) }}
              </div>
            </div>
            <RevIllustration
              alt="Illustration"
              :height="56"
              src="/img/product-card/cashHandCroppedRight.svg"
              :width="34"
            />
          </div>
        </div>
      </div>
      <div class="px-16 pb-8 pt-0">
        <ProductSpecifications
          v-if="specifications"
          :specifications="specifications"
        />
      </div>

      <div
        class="flex items-center gap-16"
        :class="{ 'md:hidden': specifications }"
        data-spec="hero-explanation"
      >
        <RevIllustration
          alt="Illustration"
          :height="64"
          src="/img/product-card/cashHandCroppedBottomLeft.svg"
          :width="64"
        />

        <div>
          <div class="text-static-brand-mid body-2-bold">
            {{ i18n(translations.explanationTitle) }}
          </div>

          <div class="text-static-brand-mid body-2">
            {{ i18n(translations.explanationBody) }}
          </div>
        </div>
      </div>
    </div>
  </RevCard>
</template>

<script setup lang="ts">
import { ref } from 'vue'

import type { MonetaryAmount } from '@backmarket/http-api'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevButtonTiny } from '@ds/components/ButtonTiny'
import { RevCard } from '@ds/components/Card'
import { RevIllustration } from '@ds/components/Illustration'
import { RevLink } from '@ds/components/Link'
import { IconChevronRight } from '@ds/icons/IconChevronRight'

import PriceWithCrossedPrice from '~/scopes/recommendation/components/PriceWithCrossedPrice.vue'
import ProductCardRatings from '~/scopes/recommendation/components/ProductCardRatings.vue'

import ColorSwatches from './ColorSwatches.vue'
import translations from './HighlightedProductCard.translations'
import ProductSpecifications, { type Spec } from './ProductSpecifications.vue'

defineProps<{
  specifications?: Spec[]
  description?: string
  warranty?: string
  imageAlt: string
  imageSrc: string
  url: string
  price: MonetaryAmount
  reviewRating: { count: number; average: number }
  title: string
  colorSwatches?: Array<{ name: string; value: string }>
  storageTags: Array<{ name: string; value: string }>
  percentageDifference: number
}>()

const i18n = useI18n()
const hasFocus = ref(false)
</script>
